/**
 * El home de la tienda: que tarjetas van a la vidriera (migracion 0093).
 *
 * DOS LLAVES
 * ----------
 * `publicado` dice que el producto esta en la tienda y se encuentra buscando o
 * por categoria. `en_home` dice que ademas esta en la vidriera, sin buscar
 * nada. Un vidrio templado se publica pero no ocupa el home.
 *
 * SE MARCA LA TARJETA, NO LA FILA
 * -------------------------------
 * El cliente ve tarjetas: el iPhone 17 Pro con todas sus memorias es UNA. Por
 * eso la pantalla de configuracion mete o saca la tarjeta entera (todos sus
 * ids), y una tarjeta esta en el home si ALGUNO de sus productos lo esta: una
 * memoria nueva de un modelo que ya esta en el home aparece sola, sin tocar
 * nada.
 *
 * Las secciones y las tarjetas se arman con las mismas funciones que la tienda
 * (`agruparEnSecciones`, `agruparGeneraciones`), asi lo que se configura es
 * exactamente lo que se ve.
 */
import {
  agruparEnSecciones,
  agruparGeneraciones,
  CATEGORIA_ACCESORIOS,
  nombreLegible,
  nombreLimpio,
  paraBuscar,
  type GrupoGeneracion,
  type NegocioPublico,
  type ProductoPublico,
  type SeccionConfigurada,
} from "./logica";

// ─────────────────────────────────────────────────────────────────────────────
// El encabezado
// ─────────────────────────────────────────────────────────────────────────────

/**
 * El titulo de siempre. Decia "iPhone sellados y usados"; con Mac, iPad, Watch
 * y AirPods publicados, un titular que nombra un solo producto se leia como una
 * pagina a medio terminar.
 */
export const TITULO_DE_SIEMPRE = "Apple sellado y usado, con garantía";

type DatosDeBajada = Partial<Pick<NegocioPublico, "localidad" | "garantia_usado_meses">>;

/**
 * La bajada de siempre, armada con los datos del local. Recorta la localidad:
 * viene tipeada a mano y un espacio al final sale como "En Carlos Pellegrini .".
 */
export function bajadaDeSiempre(n: DatosDeBajada | null | undefined): string {
  const lugar = n?.localidad?.trim() ? `En ${n.localidad.trim()}. ` : "";
  const garantia = n?.garantia_usado_meses
    ? `, con ${n.garantia_usado_meses} meses de garantía escrita`
    : "";
  return `${lugar}Te lo entregamos configurado${garantia}.`;
}

/** Lo que dice el home: lo cargado, o lo de siempre si no hay nada. */
export function encabezadoDelHome(
  n:
    | (DatosDeBajada & Partial<Pick<NegocioPublico, "titulo_home" | "bajada_home">>)
    | null
    | undefined,
): { titulo: string; bajada: string; propio: boolean } {
  const titulo = n?.titulo_home?.trim() || null;
  const bajada = n?.bajada_home?.trim() || null;
  return {
    titulo: titulo ?? TITULO_DE_SIEMPRE,
    bajada: bajada ?? bajadaDeSiempre(n),
    propio: titulo !== null || bajada !== null,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Las tarjetas
// ─────────────────────────────────────────────────────────────────────────────

export interface TarjetaDelHome {
  /** Unica dentro de su seccion. */
  clave: string;
  nombre: string;
  /** El que da la foto. */
  principal: ProductoPublico;
  /** El precio mas bajo de la tarjeta, en pesos. */
  desde: number;
  /** Todos los productos que la tarjeta contiene: se marcan juntos. */
  ids: number[];
  /**
   * Los nombres de todas sus filas. El buscador de Destacados miraba solo el
   * del principal (el mas barato, «IPHONE 17 256 GB»), y «17 pro» o «pro max»
   * no encontraban la tarjeta del iPhone 17, que tiene el Pro y el Pro Max.
   */
  nombresDeFilas: string[];
  enHome: boolean;
}

export interface SeccionDelHome {
  /** "en-local", "rubro:<slug>", "usados" o "accesorios". */
  clave: string;
  titulo: string;
  tarjetas: TarjetaDelHome[];
}

function deGrupo(g: GrupoGeneracion): TarjetaDelHome {
  // `todas` y no `capacidades`: una memoria cargada dos veces (con y sin
  // color) es un solo boton en la ficha, pero las dos filas son de la tarjeta.
  const productos = g.variantes.flatMap((v) => v.todas);
  return {
    clave: g.clave,
    nombre: g.nombre,
    principal: g.principal,
    desde: g.desde,
    ids: productos.map((p) => p.id),
    nombresDeFilas: productos.map((p) => p.nombre),
    enHome: productos.some((p) => p.en_home),
  };
}

function deProducto(p: ProductoPublico): TarjetaDelHome {
  return {
    clave: `#${p.id}`,
    nombre: nombreLegible(nombreLimpio(p.nombre)),
    principal: p,
    desde: Number(p.precio_ars) || 0,
    ids: [p.id],
    nombresDeFilas: [p.nombre],
    enHome: p.en_home,
  };
}

/**
 * Las secciones del home en el orden en que se leen, con TODAS sus tarjetas
 * publicadas, esten o no en el home. Es lo que muestra la pantalla de
 * configuracion; la tienda se queda con las marcadas (`idsDelHome`).
 */
export function seccionesDelHome(productos: readonly ProductoPublico[]): SeccionDelHome[] {
  const s = agruparEnSecciones(productos);
  const secciones: SeccionDelHome[] = [
    {
      clave: "en-local",
      titulo: "En stock",
      tarjetas: agruparGeneraciones(s.enLocal).map(deGrupo),
    },
    ...s.porRubro.map((r) => ({
      clave: `rubro:${r.slug}`,
      titulo: r.nombre,
      tarjetas: agruparGeneraciones(r.productos).map(deGrupo),
    })),
    {
      clave: "usados",
      titulo: "Usados",
      // Igual que la tienda: los modelos de la lista agrupados por generacion,
      // y las unidades con ficha una por una, abajo.
      tarjetas: [
        ...agruparGeneraciones(s.usados.filter((p) => !p.es_unidad)).map(deGrupo),
        ...s.usados.filter((p) => p.es_unidad).map(deProducto),
      ],
    },
    { clave: "accesorios", titulo: "Accesorios", tarjetas: s.accesorios.map(deProducto) },
  ];
  return secciones.filter((x) => x.tarjetas.length > 0);
}

/**
 * La categoria "Todo": el catalogo entero, sin el recorte del home. `null`
 * sigue siendo el home. Un slug de rubro nunca es "*".
 */
export const TODO_EL_CATALOGO = "*";

/** Los ids de todos los productos que van al home, tarjeta por tarjeta. */
export function idsDelHome(productos: readonly ProductoPublico[]): Set<number> {
  const ids = new Set<number>();
  for (const seccion of seccionesDelHome(productos)) {
    for (const t of seccion.tarjetas) if (t.enHome) t.ids.forEach((id) => ids.add(id));
  }
  return ids;
}

/** "6 de 24 en el home", o que la seccion no aparece. */
export function resumenDeSeccion(seccion: SeccionDelHome): string {
  const en = seccion.tarjetas.filter((t) => t.enHome).length;
  if (en === 0) return "No aparece en el home";
  if (en === seccion.tarjetas.length) {
    return en === 1 ? "Está en el home" : `Las ${en} están en el home`;
  }
  return `${en} de ${seccion.tarjetas.length} en el home`;
}

/**
 * Cuantas tarjetas de cada seccion quedan afuera del home, por clave de
 * seccion. La tienda lo usa para cerrar una seccion recortada con "Ver todo".
 */
export function afueraDelHome(productos: readonly ProductoPublico[]): Map<string, number> {
  return new Map(
    seccionesDelHome(productos).map((s) => [s.clave, s.tarjetas.filter((t) => !t.enHome).length]),
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Lo que se acomoda a mano (0095)
// ─────────────────────────────────────────────────────────────────────────────

/** La clave de una seccion propia en el orden del home (0104). */
export function clavePropia(id: number): string {
  return `propia:${id}`;
}

export function esPropia(clave: string): boolean {
  return clave.startsWith("propia:");
}

/**
 * Los grupos de la tienda de una seccion propia, en el orden elegido. Una
 * tarjeta es la generacion entera aunque sus productos esten en secciones
 * distintas (el 17 Pro en el local y el 17 a pedido): en la seccion, es una.
 * El lugar de la tarjeta es el de su producto que aparece primero.
 */
export function gruposDeSeccion(
  productos: readonly ProductoPublico[],
  ids: readonly number[],
): GrupoGeneracion[] {
  const lugarDe = new Map(ids.map((id, i) => [id, i]));
  const elegidos = productos.filter((p) => lugarDe.has(p.id));
  const lugar = (g: GrupoGeneracion): number =>
    Math.min(...g.variantes.flatMap((v) => v.todas.map((p) => lugarDe.get(p.id) ?? 1e9)));
  return agruparGeneraciones(elegidos).sort((a, b) => lugar(a) - lugar(b));
}

/** Lo mismo como tarjetas de la pantalla de configuracion. */
export function tarjetasDeSeccion(
  productos: readonly ProductoPublico[],
  ids: readonly number[],
): TarjetaDelHome[] {
  return gruposDeSeccion(productos, ids).map((g) => ({ ...deGrupo(g), enHome: true }));
}

/** Si una seccion va al home: apagarla la saca, pero lo que tiene sigue publicado. */
export function seccionVisible(
  clave: string,
  config: readonly SeccionConfigurada[] | null | undefined,
): boolean {
  return config?.find((c) => c.clave === clave)?.visible !== false;
}

/**
 * Todo lo que se puede destacar: cada tarjeta de la tienda una sola vez. Una
 * generacion partida entre el local y el pedido se junta, igual que al
 * destacarla.
 */
export function candidatosADestacar(productos: readonly ProductoPublico[]): TarjetaDelHome[] {
  const porClave = new Map<string, TarjetaDelHome>();
  for (const s of seccionesDelHome(productos)) {
    for (const t of s.tarjetas) {
      const ya = porClave.get(t.clave);
      if (!ya) porClave.set(t.clave, { ...t, ids: [...t.ids] });
      else {
        ya.ids = [...new Set([...ya.ids, ...t.ids])];
        if (t.desde < ya.desde) {
          ya.desde = t.desde;
          ya.principal = t.principal;
        }
      }
    }
  }
  return [...porClave.values()];
}

/** El nombre de una tarjeta en la tienda: el cargado, o el automatico. */
export function nombreEnTienda(
  t: { clave: string; nombre: string },
  nombres: Record<string, string> | null | undefined,
): string {
  return nombres?.[t.clave]?.trim() || t.nombre;
}

/**
 * Si la tarjeta es lo que se busca, como en la tienda (`filtrarCatalogo`):
 * palabra por palabra, sin acentos ni mayusculas, contra el nombre que muestra
 * y los de todas sus filas. Antes era la frase entera contra el nombre del
 * principal, y el ejemplo del propio buscador («17 pro») no traia el iPhone 17.
 */
export function tarjetaCoincide(
  t: TarjetaDelHome,
  texto: string,
  nombres: Record<string, string> | null | undefined,
): boolean {
  const palabras = paraBuscar(texto).split(/\s+/).filter(Boolean);
  const donde = paraBuscar([nombreEnTienda(t, nombres), t.nombre, ...t.nombresDeFilas].join(" "));
  return palabras.every((w) => donde.includes(w));
}

/**
 * Las claves de las secciones en el orden del home: las que el local acomodo,
 * en su orden, y las que no toco EN SU LUGAR DE SIEMPRE, relativo a sus
 * vecinas. Una seccion que ya no existe (un rubro vacio) se saltea sin romper
 * el resto.
 *
 * Antes las que no estaban acomodadas iban al final. «En stock» aparece recien
 * cuando entra un iPhone al local, asi que nunca estaba en lo acomodado: el
 * sellado que entraba se iba de la seccion de iPhone (va a «En stock») y caia
 * al fondo del home, debajo de veinte accesorios. Ahora cada una entra justo
 * despues de la que la precede en el orden de siempre: «En stock» despues de
 * las secciones propias, «Usados» despues del ultimo rubro. Si no la precede
 * ninguna, al principio.
 */
export function ordenDeSecciones(
  claves: readonly string[],
  config: readonly SeccionConfigurada[] | null | undefined,
): string[] {
  const hay = new Set(claves);
  const orden = (config ?? []).map((c) => c.clave).filter((c) => hay.has(c));
  const vistos = new Set(orden);
  // En el orden de siempre: dos seguidas sin acomodar quedan en su orden,
  // porque la de adelante ya esta puesta cuando llega la de atras.
  claves.forEach((clave, i) => {
    if (vistos.has(clave)) return;
    const anterior = claves
      .slice(0, i)
      .reverse()
      .find((c) => vistos.has(c));
    const donde = anterior === undefined ? 0 : orden.indexOf(anterior) + 1;
    orden.splice(donde, 0, clave);
    vistos.add(clave);
  });
  return orden;
}

/**
 * Las categorias del menu y del pie en el orden de las secciones del home.
 *
 * Venian en el orden de aparicion en la consulta, y el primer producto del
 * rubro Apple es el AirTag: «Accesorios» quedaba segunda, antes que los
 * iPhone, y el menu no seguia lo que el local acomodo en /tienda. Ahora cada
 * categoria va donde va su seccion (`rubro:<slug>`, y la de accesorios donde
 * va «Accesorios»), y la que no tiene seccion queda en su lugar de siempre.
 */
export function ordenDeCategorias<C extends { slug: string }>(
  categorias: readonly C[],
  config: readonly SeccionConfigurada[] | null | undefined,
): C[] {
  const claveDe = (c: C): string =>
    c.slug === CATEGORIA_ACCESORIOS ? "accesorios" : `rubro:${c.slug}`;
  const porClave = new Map(categorias.map((c) => [claveDe(c), c]));
  // Las claves en el orden de siempre del home, con «En stock» y «Usados»,
  // que no son categorias, para que lo no acomodado caiga en el mismo lugar
  // que su seccion: accesorios despues de los rubros y de los usados.
  const claves = [
    "en-local",
    ...categorias.filter((c) => c.slug !== CATEGORIA_ACCESORIOS).map(claveDe),
    "usados",
    "accesorios",
  ];
  return ordenDeSecciones(claves, config).flatMap((k) => {
    const c = porClave.get(k);
    return c ? [c] : [];
  });
}

/**
 * La lista que se guarda, con las secciones que hoy no se ven.
 *
 * La pantalla de configuracion guarda la lista entera de `secciones_home` con
 * las secciones que ve: las de los rubros con algo publicado. Un rubro oculto
 * un rato (o una seccion cuyas tarjetas estan todas en una propia) no estaba,
 * y cambiar cualquier otra cosa lo borraba de la config: al volver a
 * publicarlo aparecia al final y con el nombre de siempre, sin el titulo que
 * le habia puesto el local. Aca vuelven, cada una detras de la que tenia
 * adelante, asi `ordenDeSecciones` las pone donde estaban cuando reaparecen.
 *
 * Una seccion propia que falta si se va: esas se muestran siempre, aunque
 * esten vacias, asi que si no esta es que se borro.
 */
export function conLasQueNoSeVen(
  nuevas: readonly SeccionConfigurada[],
  antes: readonly SeccionConfigurada[] | null | undefined,
): SeccionConfigurada[] {
  const estan = new Set(nuevas.map((c) => c.clave));
  const detrasDe = new Map<string | null, SeccionConfigurada[]>();
  let ancla: string | null = null;
  for (const c of antes ?? []) {
    if (estan.has(c.clave)) {
      ancla = c.clave;
      continue;
    }
    if (esPropia(c.clave)) continue;
    detrasDe.set(ancla, [...(detrasDe.get(ancla) ?? []), c]);
  }
  return [
    ...(detrasDe.get(null) ?? []),
    ...nuevas.flatMap((c) => [c, ...(detrasDe.get(c.clave) ?? [])]),
  ];
}

/** El titulo de una seccion: el propio, o el de siempre. */
export function tituloDeSeccion(
  clave: string,
  deSiempre: string,
  config: readonly SeccionConfigurada[] | null | undefined,
): string {
  return config?.find((c) => c.clave === clave)?.titulo?.trim() || deSiempre;
}
